// File: frontend/src/components/ums/UserFilters.js

import React, { useState } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import Dropdown from '../common/Dropdown';
import styles from './UserFilters.module.css';

const UserFilters = ({ filters, onFilterChange, onApply, onReset, isLoading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // ✅ Track which dropdown is open

  const handleInputChange = (key, value) => {
    onFilterChange(key, value);
  };

  const handleApply = () => {
    onApply();
    setOpenDropdown(null); // ✅ Close all dropdowns
    // Collapse on mobile after applying
    if (window.innerWidth < 768) {
      setIsExpanded(false);
    }
  };

  const handleReset = () => {
    onReset();
    setOpenDropdown(null); // ✅ Close all dropdowns
    if (window.innerWidth < 768) {
      setIsExpanded(false);
    }
  };

  // ✅ Handle dropdown open/close
  const handleDropdownOpenChange = (dropdownName, isOpen) => {
    if (isOpen) {
      setOpenDropdown(dropdownName);
    } else if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    }
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'limit') return false;
    return value !== '' && value !== null && value !== undefined;
  }).length;

  // Dropdown options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'anonymous', label: 'Anonymous' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'public', label: 'Public' },
    { value: 'standard', label: 'Standard' },
    { value: 'advisor', label: 'Advisor' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' },
    { value: 'account_owner', label: 'Account Owner' },
    { value: 'super_admin', label: 'Super Admin' }
  ];

  const anonymousOptions = [
    { value: '', label: 'All Users' },
    { value: 'true', label: 'Anonymous Only' },
    { value: 'false', label: 'Authenticated Only' }
  ];

  const journeyOptions = [
    { value: '', label: 'All Stages' },
    { value: 'visitor', label: 'Visitor' },
    { value: 'signup_started', label: 'Sign Up Started' },
    { value: 'account_activated', label: 'Account Activated' },
    { value: 'active', label: 'Active' }
  ];

  const limitOptions = [
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  return (
    <div className={styles.filtersCard}>
      {/* Header - Always Visible */}
      <div className={styles.filtersHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.headerLeft}>
          <Filter size={20} />
          <h2 className={styles.filtersTitle}>Filters</h2>
          {activeFilterCount > 0 && (
            <span className={styles.filterCount}>{activeFilterCount}</span>
          )}
        </div>
        <button 
          type="button"
          className={styles.toggleButton}
          aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {/* Filter Content - Collapsible on Mobile */}
      <div className={`${styles.filtersContent} ${isExpanded ? styles.expanded : ''}`}>
        <div className={styles.filtersGrid}>
          {/* Email Address */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-email" className={styles.filterLabel}>
              Email Address
            </label>
            <input
              id="filter-email"
              type="email"
              placeholder="Search by email"
              value={filters.email_address}
              onChange={(e) => handleInputChange('email_address', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>

          {/* Phone Number */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-phone" className={styles.filterLabel}>
              Phone Number
            </label>
            <input
              id="filter-phone"
              type="tel"
              placeholder="Search by phone"
              value={filters.phone_number}
              onChange={(e) => handleInputChange('phone_number', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>

          {/* User Status */}
          <Dropdown
            label="User Status"
            value={filters.user_status}
            onChange={(value) => handleInputChange('user_status', value)}
            options={statusOptions}
            disabled={isLoading}
            name="user_status"
            isOpen={openDropdown === 'user_status'}
            onOpenChange={(isOpen) => handleDropdownOpenChange('user_status', isOpen)}
          />

          {/* User Role */}
          <Dropdown
            label="User Role"
            value={filters.user_role}
            onChange={(value) => handleInputChange('user_role', value)}
            options={roleOptions}
            disabled={isLoading}
            name="user_role"
            isOpen={openDropdown === 'user_role'}
            onOpenChange={(isOpen) => handleDropdownOpenChange('user_role', isOpen)}
          />

          {/* Anonymous */}
          <Dropdown
            label="Anonymous"
            value={filters.is_anonymous}
            onChange={(value) => handleInputChange('is_anonymous', value)}
            options={anonymousOptions}
            disabled={isLoading}
            name="is_anonymous"
            isOpen={openDropdown === 'is_anonymous'}
            onOpenChange={(isOpen) => handleDropdownOpenChange('is_anonymous', isOpen)}
          />

          {/* Journey Stage */}
          <Dropdown
            label="Journey Stage"
            value={filters.journey_stage}
            onChange={(value) => handleInputChange('journey_stage', value)}
            options={journeyOptions}
            disabled={isLoading}
            name="journey_stage"
            isOpen={openDropdown === 'journey_stage'}
            onOpenChange={(isOpen) => handleDropdownOpenChange('journey_stage', isOpen)}
          />

          {/* Start Date */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-start-date" className={styles.filterLabel}>
              Start Date
            </label>
            <input
              id="filter-start-date"
              type="date"
              value={filters.start_date}
              onChange={(e) => handleInputChange('start_date', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>

          {/* End Date */}
          <div className={styles.filterGroup}>
            <label htmlFor="filter-end-date" className={styles.filterLabel}>
              End Date
            </label>
            <input
              id="filter-end-date"
              type="date"
              value={filters.end_date}
              onChange={(e) => handleInputChange('end_date', e.target.value)}
              className={styles.filterInput}
              disabled={isLoading}
            />
          </div>

          {/* Limit */}
          <Dropdown
            label="Results Per Page"
            value={filters.limit}
            onChange={(value) => handleInputChange('limit', value)}
            options={limitOptions}
            disabled={isLoading}
            name="limit"
            isOpen={openDropdown === 'limit'}
            onOpenChange={(isOpen) => handleDropdownOpenChange('limit', isOpen)}
          />
        </div>

        {/* Actions */}
        <div className={styles.filterActions}>
          <button
            type="button"
            onClick={handleApply}
            disabled={isLoading}
            className={styles.applyButton}
          >
            {isLoading ? 'Applying...' : 'Apply Filters'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isLoading}
            className={styles.resetButton}
          >
            <X size={16} />
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserFilters;